/**
 * The upload flow, from picking files to handing them to the upload manager:
 * "Upload Files & Folders" → "where do these go?" (an existing deal, when
 * there is one) → naming a new deal. Shared by the Data Sources page and the
 * Files screens so both open the same dialogs in place.
 *
 * The host renders `element` once and calls `start()`; what happens after the
 * files are handed off — go to chat, go to the deal — is the host's
 * `onComplete`.
 */
import React, { useCallback, useState } from "react";
import DealDestinationDialog from "../components/AddFiles/DealDestinationDialog";
import FileSelectionPopup from "../components/FileSelectionPopup";
import ProjectAndReportingPeriodSelection from "../components/Upload/ProjectAndReportingPeriodSelection";
import { USER_FILES_UPDATED_EVENT } from "../constants/events";
import { canAddFilesTo, useAddFiles } from "../context/AddFilesContext";
import { useProject } from "../context/ProjectContext";
import { useToast } from "../context/ToastContext";
import { useUploadManager } from "../context/UploadManager";
import type { FolderTreeNode } from "../services/fileApi";
import {
	getFolderTreeSnapshot,
	loadFolderTree,
} from "../services/folderTreeStore";
import { projectService } from "../services/projectService";

type Step = "none" | "files" | "destination" | "new-deal";

export interface UploadDestination {
	projectId: string;
	projectName: string;
	/** The deal's root folder. */
	folderId: number;
}

export interface UploadFlow {
	start: () => void;
	element: React.ReactNode;
}

export function useUploadFlow(
	onComplete: (destination: UploadDestination) => void,
): UploadFlow {
	const [step, setStep] = useState<Step>("none");
	const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
	// Deals the picked files could be added to instead of starting a new one
	// (DOP-608). Fetched when the files are picked, so the list is current.
	const [deals, setDeals] = useState<FolderTreeNode[]>([]);
	const [loadingDeals, setLoadingDeals] = useState(false);
	const addFiles = useAddFiles();
	// The upload itself belongs to the app-wide manager, so it keeps running
	// wherever the user goes next.
	const { enqueueUpload } = useUploadManager();
	const { setSelectedProject } = useProject();
	const { showToast } = useToast();

	const start = useCallback(() => setStep("files"), []);
	const close = useCallback(() => setStep("none"), []);
	const startNewDeal = useCallback(() => setStep("new-deal"), []);

	/**
	 * Picked files first answer "where do these go?" — an existing deal or a
	 * new one. With no deal to add to (or the list unavailable) that question
	 * has one answer, so it goes straight to naming the new deal.
	 */
	const handleSelectedFiles = (files: File[]) => {
		setSelectedFiles(files);
		if (!addFiles) {
			startNewDeal();
			return;
		}
		setStep("destination");
		// The deals the Deal Room already loaded show at once; the request
		// below only confirms them (or is answered from that same tree).
		const known = getFolderTreeSnapshot().tree;
		if (known) {
			setDeals(known.filter(canAddFilesTo));
			setLoadingDeals(false);
		} else {
			setLoadingDeals(true);
		}
		// Only move on if the chooser is still what the user is looking at.
		const skipChooser = () =>
			setStep((current) => (current === "destination" ? "new-deal" : current));
		loadFolderTree()
			.then((tree) => {
				const eligible = tree.filter(canAddFilesTo);
				setDeals(eligible);
				if (eligible.length === 0) skipChooser();
			})
			.catch((error) => {
				console.error("Could not load deals for the chooser:", error);
				// Deals already on screen from the shared tree stay choosable.
				if (!known?.some(canAddFilesTo)) skipChooser();
			})
			.finally(() => setLoadingDeals(false));
	};

	const handleChooseDeal = (deal: FolderTreeNode) => {
		if (!addFiles) return;
		setStep("none");
		addFiles.openAddFiles({
			deal,
			files: selectedFiles,
			onAdded: () => {
				setSelectedFiles([]);
				const projectId = deal.folder.project_id;
				if (!projectId) return;
				setSelectedProject({ id: projectId, name: deal.folder.name });
				onComplete({
					projectId,
					projectName: deal.folder.name,
					folderId: deal.folder.id,
				});
			},
			onChangeDeal: (files) => {
				setSelectedFiles(files);
				setStep("destination");
			},
		});
	};

	const handleNewDeal = async (projectName: string, reportingPeriod: string) => {
		try {
			const project = await projectService.createProjectWithFolder({
				name: projectName,
			});
			setSelectedProject({ id: project.project_id, name: projectName });
			setStep("none");
			// The new deal's folder should appear everywhere right away.
			window.dispatchEvent(new CustomEvent(USER_FILES_UPDATED_EVENT));
			// The manager starts the upload now or queues it, and reports the
			// outcome in the upload tray.
			enqueueUpload({
				projectId: project.project_id,
				projectName,
				folderId: project.folder_id,
				reportingPeriod,
				files: selectedFiles,
			});
			setSelectedFiles([]);
			onComplete({
				projectId: project.project_id,
				projectName,
				folderId: project.folder_id,
			});
		} catch (error) {
			// No project means no folder to upload into: say so and stop.
			console.error("Project creation failed:", error);
			setStep("none");
			showToast(
				`Could not create "${projectName}" — nothing was uploaded. Please try again.`,
			);
		}
	};

	const element = (
		<>
			<FileSelectionPopup
				onClose={close}
				onFileSelected={handleSelectedFiles}
				isOpen={step === "files"}
			/>
			{step === "destination" && (
				<DealDestinationDialog
					files={selectedFiles}
					deals={deals}
					loading={loadingDeals}
					onClose={close}
					onChooseDeal={handleChooseDeal}
					onStartNew={startNewDeal}
				/>
			)}
			<ProjectAndReportingPeriodSelection
				isOpen={step === "new-deal"}
				onClose={close}
				onSelect={handleNewDeal}
			/>
		</>
	);

	return { start, element };
}
